import { CollapsibleReference } from '@/components/CollapsibleReference'
import { InfoTooltip } from '@/components/InfoTooltip'
import { Select, type SelectOption } from '@/components/Select'
import { DOCUMENT_PROMPT_EMPTY_PLACEHOLDER, NOT_ATTACHED_TO_ANY_PRODUCT_TEXT, PROMPT_MODE_HELPER_TEXT, PROMPT_MODE_OPTIONS } from '../labels'

export interface PromptTabProps {
  /** Every product type whose document_requirements include this document
   * type - empty when none do yet (Design PRD 10's many-to-many resolution). */
  referencingProducts: SelectOption[]
  selectedProductId: string
  onSelectedProductIdChange: (productId: string) => void
  /** The selected product's own Baseline Prompt text, fetched by the parent
   * screen (PRD 00: shared components don't fetch their own data). */
  baselineReference: { content?: string; isLoading: boolean; isError: boolean }
  mode: 'supplement' | 'override'
  onModeChange: (mode: 'supplement' | 'override') => void
  documentPrompt: string
  onDocumentPromptChange: (value: string) => void
}

// Field order matches Design PRD 10 exactly: the "Preview against" picker
// comes first since it drives what the reference block below it shows; the
// "Baseline prompt" block sits directly above the Prompt Mode selector; the
// Resolved Prompt Preview (a separate component) is placed at the bottom of
// the tab, below this one.
export function PromptTab({
  referencingProducts,
  selectedProductId,
  onSelectedProductIdChange,
  baselineReference,
  mode,
  onModeChange,
  documentPrompt,
  onDocumentPromptChange,
}: PromptTabProps) {
  return (
    <div className="flex flex-col gap-6">
      <section className="flex flex-col gap-4 rounded-control border border-border bg-surface p-4">
        {referencingProducts.length === 0 ? (
          <p className="text-meta text-text-disabled">{NOT_ATTACHED_TO_ANY_PRODUCT_TEXT}</p>
        ) : (
          <div className="flex flex-col gap-1">
            <label className="flex items-center gap-1 text-meta font-medium text-text-secondary" htmlFor="preview-against-product">
              Preview against
              <InfoTooltip text="Which product's baseline prompt to show below and to resolve the preview against - a document type can be used by more than one product." />
            </label>
            <Select
              id="preview-against-product"
              value={selectedProductId}
              onChange={onSelectedProductIdChange}
              options={referencingProducts}
              className="w-72"
            />
          </div>
        )}

        <CollapsibleReference
          label="Baseline prompt"
          content={baselineReference.content}
          isLoading={baselineReference.isLoading}
          isError={baselineReference.isError}
          emptyText="No baseline prompt set for this product."
        />

        <div className="flex flex-col gap-1">
          <label className="flex items-center gap-1 text-meta font-medium text-text-secondary" htmlFor="prompt-mode">
            Prompt Mode
            <InfoTooltip text="Whether this document type's prompt text is appended to the baseline above, or replaces it entirely." />
          </label>
          <Select
            id="prompt-mode"
            value={mode}
            onChange={(value) => onModeChange(value as 'supplement' | 'override')}
            options={PROMPT_MODE_OPTIONS}
            className="w-72"
          />
        </div>

        <div className="flex flex-col gap-1">
          <label className="flex items-center gap-1 text-meta font-medium text-text-secondary" htmlFor="document-prompt">
            Document Prompt
            <InfoTooltip text="Prompt text specific to this document type, combined with the product's baseline prompt per the Prompt Mode above." />
          </label>
          <textarea
            id="document-prompt"
            rows={6}
            value={documentPrompt}
            onChange={(event) => onDocumentPromptChange(event.target.value)}
            placeholder={DOCUMENT_PROMPT_EMPTY_PLACEHOLDER}
            className="rounded-control border border-border bg-surface px-3 py-2 text-body text-text-primary focus:border-accent focus:outline-none disabled:opacity-50"
          />
          <span className="text-meta text-text-secondary">{PROMPT_MODE_HELPER_TEXT[mode]}</span>
        </div>
      </section>
    </div>
  )
}
